import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { FileBarChart, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import api from "@/lib/api";
import { money, dateFmt } from "@/lib/format";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AdminReports() {
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/reports/transactions", { params: { start, end } })
      .then(({ data }) => setData(data)).catch(() => toast.error("Load failed"));
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const stat = (label, val, Icon, color) => (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider"><Icon className={`w-4 h-4 ${color}`} /> {label}</div>
      <div className={`font-mono-fin text-2xl font-bold mt-2 ${color}`}>{val}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2"><FileBarChart className="w-6 h-6 text-amber-400" /> Deposit & Withdrawal Report</h1>

      <div className="glass rounded-2xl p-5 flex flex-wrap items-end gap-4">
        <div><label className="text-xs text-amber-400 uppercase tracking-wider">From</label>
          <input data-testid="report-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)}
            className="mt-1 block bg-[#0D0E12] border border-amber-500/20 rounded-xl px-4 py-2.5 outline-none text-sm" /></div>
        <div><label className="text-xs text-amber-400 uppercase tracking-wider">To</label>
          <input data-testid="report-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)}
            className="mt-1 block bg-[#0D0E12] border border-amber-500/20 rounded-xl px-4 py-2.5 outline-none text-sm" /></div>
        <button data-testid="report-apply-btn" onClick={load} className="gold-btn px-5 py-2.5 rounded-xl text-sm font-semibold">Apply</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stat("Deposits", money(data?.deposit_total || 0), ArrowDownToLine, "text-emerald-400")}
        {stat("Withdrawals Paid", money(data?.withdrawal_paid_total || 0), ArrowUpFromLine, "text-red-400")}
        {stat("Withdrawals Pending", money(data?.withdrawal_pending_total || 0), ArrowUpFromLine, "text-amber-400")}
      </div>

      <div className="glass rounded-2xl p-6 overflow-x-auto" data-testid="report-deposits-table">
        <h2 className="font-display font-bold mb-3 text-emerald-400">Deposits ({data?.deposit_count || 0})</h2>
        <table className="w-full text-sm">
          <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
            <th className="text-left py-2">User</th><th className="text-right py-2">Amount</th><th className="text-left py-2">Note</th><th className="text-right py-2">Date</th>
          </tr></thead>
          <tbody>
            {(data?.deposits || []).map((d) => (
              <tr key={d.id} className="border-b border-slate-800/60">
                <td className="py-2 text-slate-300">{d.user_email}</td>
                <td className="py-2 text-right font-mono-fin text-emerald-400">{money(d.amount)}</td>
                <td className="py-2 text-slate-500 text-xs">{d.note || (d.auto ? "Auto-detected" : "-")}</td>
                <td className="py-2 text-right text-slate-500 text-xs">{dateFmt(d.created_at)}</td>
              </tr>
            ))}
            {(data?.deposits || []).length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-500">No deposits in range</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="glass rounded-2xl p-6 overflow-x-auto" data-testid="report-withdrawals-table">
        <h2 className="font-display font-bold mb-3 text-red-400">Withdrawals ({data?.withdrawal_count || 0})</h2>
        <table className="w-full text-sm">
          <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
            <th className="text-left py-2">User</th><th className="text-right py-2">Amount</th><th className="text-center py-2">Status</th><th className="text-right py-2">Date</th>
          </tr></thead>
          <tbody>
            {(data?.withdrawals || []).map((w) => (
              <tr key={w.id} className="border-b border-slate-800/60">
                <td className="py-2 text-slate-300">{w.user_email}</td>
                <td className="py-2 text-right font-mono-fin text-red-400">{money(w.amount)}</td>
                <td className="py-2 text-center"><span className="text-[10px] px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 uppercase">{w.status}</span></td>
                <td className="py-2 text-right text-slate-500 text-xs">{dateFmt(w.created_at)}</td>
              </tr>
            ))}
            {(data?.withdrawals || []).length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-500">No withdrawals in range</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
